import type { IServerSideDatasource, IServerSideGetRowsRequest } from "ag-grid-enterprise";
import type { AppDispatch } from "~/store";
import type { InviteIdsByEmail, UserInviteAction, UserLoginStatus } from "~admin/users/helpers/userLoginStatus";

import { UserRole, UserStatus } from "~/gql/graphqlBase.generated";
import createSsrmDatasource, { fetchAllRows } from "~/helpers/grid/createSsrmDatasource";
import { deriveUserLoginStatus, resolveUserInviteAction } from "~admin/users/helpers/userLoginStatus";
import { withoutRetiredRoles } from "~admin/users/helpers/userRoleDisplay";

export const ADMIN_USERS_GRID_ID = "adminUsers";

export interface RawAdminUserRow {
	assignedRoutes?: string[] | null;
	assignedSites?: string[] | null;
	customerId?: string | null;
	customerName?: string | null;
	email: string;
	fullName: string;
	fusionAuthUserId?: string | null;
	lastActiveAt?: string | null;
	lastLoginAt?: string | null;
	lastModified?: string | null;
	multiFactorEnabled?: boolean | null;
	multiFactorMethods?: string[] | null;
	roles?: string | string[] | null;
	status?: string | null;
	userId: string;
}

export interface AdminUserGridRow {
	assignedRoutes: string[];
	assignedSites: string[];
	customerId: string | null;
	customerName: string | null;
	email: string;
	fullName: string;
	fusionAuthUserId: string | null;
	inviteAction: UserInviteAction | null;
	lastActiveAt: string | null;
	lastLoginAt: string | null;
	lastModified: string;
	loginStatus: UserLoginStatus;
	multiFactorEnabled: boolean;
	multiFactorMethods: string[];
	roles: UserRole[];
	status: UserStatus;
	userId: string;
}

export interface RowShapingDeps {
	inviteIdsByEmail: InviteIdsByEmail;
	pendingInviteEmails: Set<string>;
}

const SUPPORTED_USER_ROLES = new Set<string>(withoutRetiredRoles(Object.values(UserRole)));

export const parseUserRoles = (roles: RawAdminUserRow["roles"]): UserRole[] => {
	const tokens = Array.isArray(roles) ? roles : typeof roles === "string" ? roles.split(",") : [];
	return tokens.map((token) => String(token).trim()).filter((token) => SUPPORTED_USER_ROLES.has(token)) as UserRole[];
};

export const normalizeUserStatus = (status: RawAdminUserRow["status"]): UserStatus =>
	String(status) === UserStatus.Inactive ? UserStatus.Inactive : UserStatus.Active;

export const toAdminUserGridRow = (raw: RawAdminUserRow, deps: RowShapingDeps): AdminUserGridRow => {
	const loginTarget = { email: raw.email, fusionAuthUserId: raw.fusionAuthUserId };
	return {
		assignedRoutes: raw.assignedRoutes ?? [],
		assignedSites: raw.assignedSites ?? [],
		customerId: raw.customerId ?? null,
		customerName: raw.customerName ?? null,
		email: raw.email,
		fullName: raw.fullName,
		fusionAuthUserId: raw.fusionAuthUserId ?? null,
		inviteAction: resolveUserInviteAction(loginTarget, deps.inviteIdsByEmail),
		lastActiveAt: raw.lastActiveAt ?? null,
		lastLoginAt: raw.lastLoginAt ?? null,
		lastModified: raw.lastModified ?? "",
		loginStatus: deriveUserLoginStatus(loginTarget, deps.pendingInviteEmails),
		multiFactorEnabled: raw.multiFactorEnabled ?? false,
		multiFactorMethods: raw.multiFactorMethods ?? [],
		roles: parseUserRoles(raw.roles),
		status: normalizeUserStatus(raw.status),
		userId: raw.userId
	};
};

export interface AdminUsersDatasourceOptions {
	getInviteIdsByEmail: () => InviteIdsByEmail;
	getPendingInviteEmails: () => Set<string>;
	getSearchText?: () => string | undefined;
}

export const createAdminUsersDatasource = (
	dispatch: AppDispatch,
	{ getInviteIdsByEmail, getPendingInviteEmails, getSearchText }: AdminUsersDatasourceOptions
): IServerSideDatasource =>
	createSsrmDatasource<AdminUserGridRow>(ADMIN_USERS_GRID_ID, dispatch, {
		getSearchText,
		postProcessRows: (rows) =>
			(rows as unknown as RawAdminUserRow[]).map((raw) =>
				toAdminUserGridRow(raw, {
					inviteIdsByEmail: getInviteIdsByEmail(),
					pendingInviteEmails: getPendingInviteEmails()
				})
			)
	});

const FETCH_ALL_REQUEST: IServerSideGetRowsRequest = {
	endRow: 0,
	filterModel: null,
	groupKeys: [],
	pivotCols: [],
	pivotMode: false,
	rowGroupCols: [],
	sortModel: [{ colId: "fullName", sort: "asc" }],
	startRow: 0,
	valueCols: []
};

export const fetchAllAdminUserRows = async (
	dispatch: AppDispatch,
	abortRef: { current: boolean }
): Promise<RawAdminUserRow[]> => {
	const { rows } = await fetchAllRows<RawAdminUserRow>(ADMIN_USERS_GRID_ID, FETCH_ALL_REQUEST, { abortRef, dispatch });
	return rows;
};
